/**
 * Community Presets Browser
 * Browse, download, and share guitar effect presets with the community
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Search,
  Download,
  Share2,
  Star,
  TrendingUp,
  Filter,
  Grid,
  List,
  DollarSign,
  Flag,
  Heart,
  Play,
  Pause,
  Users,
  ChevronDown,
  X,
  Check,
  Loader2,
  ExternalLink,
  CreditCard,
  Volume2,
} from 'lucide-react';
import { PRESET_CATEGORIES } from './GuitarPresets';
import { PurchaseModal } from '../../payments/PayPalPayments';
import { PresetPreviewButton } from '../../marketplace/PresetPreviewButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star rating display
const StarRating = ({ rating, count, size = 'sm' }) => {
  const stars = Math.round(rating);
  const sizeClass = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            className={`${sizeClass} ${i <= stars ? 'text-amber-400 fill-amber-400' : 'text-zinc-600'}`}
          />
        ))}
      </div>
      {count > 0 && (
        <span className="text-[10px] text-zinc-500">({count})</span>
      )}
    </div>
  );
};

// Community preset card
const CommunityPresetCard = ({ 
  preset, 
  onDownload, 
  onPreview, 
  onReport,
  isDownloading,
  isOwned 
}) => {
  const colorMap = {
    orange: 'border-orange-500/30 hover:border-orange-500/50',
    red: 'border-red-500/30 hover:border-red-500/50',
    cyan: 'border-cyan-500/30 hover:border-cyan-500/50',
    blue: 'border-blue-500/30 hover:border-blue-500/50',
    pink: 'border-pink-500/30 hover:border-pink-500/50',
    violet: 'border-violet-500/30 hover:border-violet-500/50',
    amber: 'border-amber-500/30 hover:border-amber-500/50',
    emerald: 'border-emerald-500/30 hover:border-emerald-500/50',
    slate: 'border-zinc-500/30 hover:border-zinc-500/50',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-zinc-900/50 border rounded-xl p-4 transition-all ${
        colorMap[preset.color] || colorMap.slate
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{preset.icon}</span>
          <div>
            <h4 className="text-sm font-medium text-white">{preset.name}</h4>
            <p className="text-[10px] text-zinc-500 capitalize">{preset.category}</p>
          </div>
        </div>
        {preset.price > 0 ? (
          <span className="text-xs font-bold text-emerald-400 bg-emerald-500/20 px-2 py-0.5 rounded">
            ${preset.price.toFixed(2)}
          </span>
        ) : (
          <span className="text-[10px] text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">
            FREE
          </span>
        )}
      </div>

      {/* Description */}
      <p className="text-xs text-zinc-400 mb-3 line-clamp-2">{preset.description}</p>

      {/* Stats */}
      <div className="flex items-center gap-4 mb-3">
        <div className="flex items-center gap-1 text-zinc-500">
          <Download className="w-3 h-3" />
          <span className="text-[10px]">{preset.downloads || 0}</span>
        </div>
        <div className="flex items-center gap-1 text-zinc-500">
          <Play className="w-3 h-3" />
          <span className="text-[10px]">{preset.plays || 0}</span>
        </div>
        <StarRating rating={preset.rating || 0} count={preset.ratingCount || 0} />
      </div>

      {/* Creator */}
      {preset.creator && (
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-zinc-800">
          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
            <span className="text-[8px] text-white font-bold">
              {preset.creator.name?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
          <span className="text-[10px] text-zinc-400">by {preset.creator.name}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <PresetPreviewButton
          presetId={preset.id}
          size="sm"
          variant="ghost"
          className="flex-1 h-8 text-xs border border-zinc-700"
        />
        
        {isOwned ? (
          <Button
            variant="ghost"
            size="sm"
            disabled
            className="flex-1 h-8 text-xs text-green-400"
          >
            <Check className="w-3 h-3 mr-1" />
            Owned
          </Button>
        ) : (
          <Button
            onClick={() => onDownload(preset)}
            disabled={isDownloading}
            className="flex-1 h-8 text-xs bg-violet-600 hover:bg-violet-500"
          >
            {isDownloading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                <Download className="w-3 h-3 mr-1" />
                {preset.price > 0 ? 'Buy' : 'Get'}
              </>
            )}
          </Button>
        )}
      </div>

      {/* Report button */}
      <button
        onClick={() => onReport(preset)}
        className="absolute top-2 right-2 text-zinc-600 hover:text-red-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Report preset"
      >
        <Flag className="w-3 h-3" />
      </button>
    </motion.div>
  );
};

// Share preset modal
const SharePresetModal = ({ preset, isOpen, onClose, onShare, userId }) => {
  const [price, setPrice] = useState(0);
  const [description, setDescription] = useState(preset?.description || '');
  const [sharing, setSharing] = useState(false);

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await fetch(`${API_URL}/api/guitar-presets/${preset.id}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          price: price,
          description: description
        })
      });
      
      if (res.ok) {
        onShare(preset.id);
        onClose();
      }
    } catch (error) {
      console.error('Failed to share preset:', error);
    }
    setSharing(false);
  };

  if (!isOpen || !preset) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center">
            <Share2 className="w-6 h-6 text-violet-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Share Preset</h3>
            <p className="text-xs text-zinc-500">Share "{preset.name}" with the community</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Description */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Description</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your preset..."
              className="bg-zinc-800 border-zinc-700"
            />
          </div>

          {/* Price */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Price (USD)</label>
            <div className="flex gap-2">
              <Button
                variant={price === 0 ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPrice(0)}
                className="flex-1"
              >
                Free
              </Button>
              <Button
                variant={price === 0.99 ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPrice(0.99)}
                className="flex-1"
              >
                $0.99
              </Button>
              <Button
                variant={price === 2.99 ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPrice(2.99)}
                className="flex-1"
              >
                $2.99
              </Button>
              <Button
                variant={price === 4.99 ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPrice(4.99)}
                className="flex-1"
              >
                $4.99
              </Button>
            </div>
            {price > 0 && (
              <p className="text-[10px] text-zinc-500 mt-2">
                You'll earn 70% (${(price * 0.7).toFixed(2)}) per download
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <Button variant="ghost" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleShare}
            disabled={sharing}
            className="flex-1 bg-violet-600 hover:bg-violet-500"
          >
            {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Share Preset'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Report modal
const ReportModal = ({ preset, isOpen, onClose, userId }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reasons = [
    'Inappropriate name',
    'Stolen/Copied content',
    'Misleading description',
    'Broken/Not working',
    'Spam',
    'Other'
  ];

  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/guitar-presets/${preset.id}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          reason,
          details
        })
      });
      onClose();
    } catch (error) {
      console.error('Failed to submit report:', error);
    }
    setSubmitting(false);
  };

  if (!isOpen || !preset) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-semibold text-white mb-4">Report Preset</h3>
        
        <div className="space-y-3 mb-4">
          {reasons.map(r => (
            <button
              key={r}
              onClick={() => setReason(r)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                reason === r
                  ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                  : 'bg-zinc-800 border border-transparent text-zinc-400 hover:text-white'
              }`}
            >
              {r}
            </button>
          ))}
        </div>

        <Input
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Additional details (optional)"
          className="mb-4 bg-zinc-800 border-zinc-700"
        />

        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!reason || submitting}
            className="flex-1 bg-red-600 hover:bg-red-500"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Report'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/**
 * Main Community Presets Browser Component
 */
export const CommunityPresetsBrowser = ({
  userId,
  userPresets = [],
  onDownloadPreset,
  onPreviewPreset,
  onRefreshPresets,
}) => {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('downloads');
  const [priceFilter, setPriceFilter] = useState('all');
  const [downloadingId, setDownloadingId] = useState(null);
  const [sharePreset, setSharePreset] = useState(null);
  const [reportPreset, setReportPreset] = useState(null);
  const [purchasePreset, setPurchasePreset] = useState(null); // PayPal purchase modal
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Fetch community presets
  const fetchPresets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        category: selectedCategory,
        sort_by: sortBy,
        price_filter: priceFilter,
        search: searchQuery,
        page: page.toString(),
        limit: '20'
      });
      
      const res = await fetch(`${API_URL}/api/guitar-presets/community/browse?${params}`);
      if (res.ok) {
        const data = await res.json();
        setPresets(data.presets || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch community presets:', error);
    }
    setLoading(false);
  }, [selectedCategory, sortBy, priceFilter, searchQuery, page]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  // Check if user owns a preset (downloaded from community)
  const ownedPresetIds = useMemo(() => {
    return new Set(
      userPresets
        .filter(p => p.sourcePresetId)
        .map(p => p.sourcePresetId)
    );
  }, [userPresets]);

  // Handle download (free) or purchase (paid)
  const handleDownload = async (preset) => {
    if (!userId) return;
    
    // If paid preset, show PayPal purchase modal
    if (preset.price > 0) {
      setPurchasePreset(preset);
      return;
    }
    
    // Free preset - direct download
    setDownloadingId(preset.id);
    
    try {
      const res = await fetch(`${API_URL}/api/guitar-presets/${preset.id}/download?user_id=${userId}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        onDownloadPreset?.(data.preset);
        onRefreshPresets?.();
      }
    } catch (error) {
      console.error('Failed to download preset:', error);
    }
    
    setDownloadingId(null);
  };
  
  // Handle successful purchase
  const handlePurchaseComplete = async (purchaseData) => {
    if (purchasePreset) {
      // Download the preset after successful payment
      try {
        const res = await fetch(`${API_URL}/api/guitar-presets/${purchasePreset.id}/download?user_id=${userId}`, {
          method: 'POST'
        });
        
        if (res.ok) {
          const data = await res.json();
          onDownloadPreset?.(data.preset);
          onRefreshPresets?.();
        }
      } catch (error) {
        console.error('Failed to download after purchase:', error);
      }
    }
    setPurchasePreset(null);
  };

  // Handle preview
  const handlePreview = (preset) => {
    onPreviewPreset?.(preset);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-violet-400" />
          <h3 className="text-lg font-semibold text-white">Community Presets</h3>
          <span className="text-xs text-zinc-500">({total} available)</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search presets..."
            className="pl-9 h-9 bg-zinc-800 border-zinc-700"
          />
        </div>

        {/* Category filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="h-9 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white"
        >
          <option value="all">All Categories</option>
          {PRESET_CATEGORIES.filter(c => c.id !== 'all').map(cat => (
            <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
          ))}
        </select>

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="h-9 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white"
        >
          <option value="downloads">Most Downloaded</option>
          <option value="rating">Top Rated</option>
          <option value="newest">Newest</option>
          <option value="price">Price: Low to High</option>
        </select>

        {/* Price filter */}
        <select
          value={priceFilter}
          onChange={(e) => setPriceFilter(e.target.value)}
          className="h-9 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white"
        >
          <option value="all">All Prices</option>
          <option value="free">Free Only</option>
          <option value="paid">Paid Only</option>
        </select>
      </div>

      {/* Presets Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
        </div>
      ) : presets.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {presets.map(preset => (
            <CommunityPresetCard
              key={preset.id}
              preset={preset}
              onDownload={handleDownload}
              onPreview={handlePreview}
              onReport={setReportPreset}
              isDownloading={downloadingId === preset.id}
              isOwned={ownedPresetIds.has(preset.id)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No community presets found</p>
          <p className="text-xs text-zinc-600 mt-1">Be the first to share your presets!</p>
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-zinc-400 py-2">
            Page {page} of {Math.ceil(total / 20)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / 20)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {sharePreset && (
          <SharePresetModal
            preset={sharePreset}
            isOpen={!!sharePreset}
            onClose={() => setSharePreset(null)}
            onShare={(id) => {
              fetchPresets();
              onRefreshPresets?.();
            }}
            userId={userId}
          />
        )}
        {reportPreset && (
          <ReportModal
            preset={reportPreset}
            isOpen={!!reportPreset}
            onClose={() => setReportPreset(null)}
            userId={userId}
          />
        )}
        {purchasePreset && (
          <PurchaseModal
            isOpen={!!purchasePreset}
            onClose={() => setPurchasePreset(null)}
            item={purchasePreset}
            itemType="preset"
            buyerId={userId}
            onPurchaseComplete={handlePurchaseComplete}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CommunityPresetsBrowser;
